import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from 'lucide-react';
import { businessCalendar } from '../../services/businessCalendar';
import './ExceptionCalendarPicker.css';

interface ExceptionCalendarPickerProps {
  totalCount: number;
  exceptionDates: string[];        // YYYY-MM-DD dates that have exceptions
  defaultDateFrom?: string;        // YYYY-MM-DD initial range start
  defaultDateTo?: string;          // YYYY-MM-DD initial range end
  selectedDate: string | null;     // Currently selected single-date filter
  onDateSelect: (date: string | null) => void;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

const toYMD = (d: Date): string =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const DAY_LABELS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'];
const MONTH_NAMES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const ExceptionCalendarPicker: React.FC<ExceptionCalendarPickerProps> = ({
  totalCount,
  exceptionDates,
  selectedDate,
  onDateSelect,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    if (selectedDate) {
      const [y, m, d] = selectedDate.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    return new Date();
  });
  const wrapperRef = useRef<HTMLDivElement>(null);

  const exceptionDateSet = new Set(exceptionDates);

  // Navigate calendar to the selected date's month when it changes externally
  useEffect(() => {
    if (selectedDate) {
      const [y, m, d] = selectedDate.split('-').map(Number);
      setViewDate(new Date(y, m - 1, d));
    }
  }, [selectedDate]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1);
  let startOffset = firstDay.getDay() - 1; // Monday=0
  if (startOffset < 0) startOffset = 6;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: (number | null)[] = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const todayYMD = toYMD(new Date());

  const handleSelect = (day: number) => {
    const dateStr = `${year}-${pad2(month + 1)}-${pad2(day)}`;
    if (dateStr === selectedDate) {
      // Clicking the already-selected date clears the filter
      onDateSelect(null);
    } else {
      onDateSelect(dateStr);
    }
    setOpen(false);
  };

  const handleShowAll = () => {
    onDateSelect(null);
    setOpen(false);
  };

  return (
    <div className="exc-calendar-wrapper" ref={wrapperRef}>
      <button
        type="button"
        className="exc-calendar-btn"
        onClick={() => setOpen(!open)}
        title={t('confirmations.exceptions.calendarTooltip', 'Filter by date')}
      >
        <Calendar size={18} />
        {totalCount > 0 && (
          <span className="exc-calendar-badge">{totalCount}</span>
        )}
      </button>

      {open && (
        <div className="exc-calendar-dropdown">
          {/* Navigation */}
          <div className="exc-calendar-nav">
            <button type="button" onClick={() => setViewDate(new Date(year, month - 1, 1))}>&lsaquo;</button>
            <span>{MONTH_NAMES[month]} {year}</span>
            <button type="button" onClick={() => setViewDate(new Date(year, month + 1, 1))}>&rsaquo;</button>
          </div>

          {/* Grid */}
          <div className="exc-calendar-grid">
            {DAY_LABELS.map(d => (
              <div key={d} className="exc-calendar-dayhead">{d}</div>
            ))}
            {cells.map((day, i) => {
              if (day === null) return <div key={`e${i}`} className="exc-calendar-cell empty" />;
              const dateStr = `${year}-${pad2(month + 1)}-${pad2(day)}`;
              const cellDate = new Date(year, month, day);
              const isNonBusiness = !businessCalendar.isBusinessDay(cellDate);
              const isSelected = dateStr === selectedDate;
              const isToday = dateStr === todayYMD;
              const hasExceptions = exceptionDateSet.has(dateStr);
              const holiday = businessCalendar.getHoliday(cellDate);

              return (
                <button
                  key={dateStr}
                  type="button"
                  className={
                    'exc-calendar-cell' +
                    (isSelected ? ' selected' : '') +
                    (isNonBusiness ? ' disabled-day' : '') +
                    (isToday ? ' today' : '') +
                    (hasExceptions && !isNonBusiness ? ' has-exceptions' : '')
                  }
                  onClick={() => handleSelect(day)}
                  disabled={isNonBusiness}
                  title={holiday ? holiday.name : undefined}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Show All / Clear filter */}
          {selectedDate && (
            <button
              type="button"
              className="exc-calendar-show-all"
              onClick={handleShowAll}
            >
              {t('confirmations.exceptions.showAll', 'Show all')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ExceptionCalendarPicker;
